import React, { useState } from 'react';
import { View, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { IconButton, Text, Portal, Modal } from 'react-native-paper';
import { Color as ThemeColors, Border } from '@/GlobalStyles';
import { useTranslation } from 'react-i18next';
import { useGetHelpTextByFieldQuery } from '@/data/data';
import { getFieldLabel } from '@/utils/helpTextsUtils';

interface HelpButtonProps {
  fieldName: string;
  size?: number;
  color?: string;
  style?: any;
}

export default function HelpButton({
  fieldName,
  size = 18,
  color = ThemeColors.MainBleu,
  style,
}: HelpButtonProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const { i18n, t } = useTranslation();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isMobile = screenWidth < 768;

  const { data, loading } = useGetHelpTextByFieldQuery({
    variables: {
      fieldName,
    },
    skip: !fieldName,
  });

  const helpText = data?.helpTexts?.[0];
  const currentLanguage = i18n.language || 'fr';
  const rawText = currentLanguage === 'en' && helpText?.textEn ? helpText.textEn : helpText?.textFr;

  // Nettoyer le texte des mentions d'administrateur
  const textToShow = rawText;

  const fieldLabel = getFieldLabel(fieldName, t);

  if (!helpText || loading) {
    return null;
  }

  return (
    <>
      <IconButton
        icon="information-outline"
        size={size}
        iconColor={color}
        onPress={() => setModalVisible(true)}
        style={[styles.button, style]}
        pointerEvents={'auto'}
      />

      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={[styles.modal]}
        >
          <View
            style={[
              styles.modalContent,
              isMobile && {
                margin: 16,
                maxHeight: screenHeight * 0.8,
              },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{fieldLabel}</Text>
              <IconButton
                icon="close"
                size={24}
                iconColor={ThemeColors.blanc}
                onPress={() => setModalVisible(false)}
                style={styles.closeButton}
              />
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.helpText}>{textToShow}</Text>
            </View>
          </View>
        </Modal>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    margin: 0,
    pointerEvents: 'auto',
  },
  modal: {
    margin: 20,
    maxWidth: Platform.OS === 'web' ? 500 : undefined,
    alignSelf: Platform.OS === 'web' ? 'center' : undefined,
    width: Platform.OS === 'web' ? '100%' : undefined,
    borderRadius: Border.br_3xs,
  },
  modalContent: {
    backgroundColor: ThemeColors.blanc,
    borderRadius: Border.br_3xs,
    borderWidth: 1,
    borderColor: ThemeColors.MainBleu,
    overflow: 'hidden',
  },
  modalContentMobile: {
    maxHeight: '100%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: ThemeColors.MainBleu,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ThemeColors.blanc,
    flex: 1,
  },
  closeButton: {
    margin: 0,
    backgroundColor: ThemeColors.blanc + '20',
  },
  modalBody: {
    padding: 20,
  },
  helpText: {
    fontSize: 15,
    lineHeight: 24,
    color: ThemeColors.MainNoir,
  },
});
